import { useRef, forwardRef, ChangeEvent, useState } from 'react';
import styles from './UploadControl.module.css';
import UploadIcon from '@mui/icons-material/FileUpload';
import {
	Button, Spacer, Caption, InfoChip, Checkbox
} from '@components';
import { BUTTON_TYPES, SPACER_DIRECTION, CHIP_TYPE } from '@types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useMediaQuery } from '@hooks';

const DEFAULT_ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/tiff', 'application/pdf'];

const MIME_LABEL_MAP: Record<string, string> = {
	'image/jpeg': 'jpg',
	'image/png': 'png',
	'image/tiff': 'tiff',
	'application/pdf': 'pdf',
	'application/csv': 'csv',
	'text/csv': 'csv',
};

const mimeToLabel = (mime: string) => MIME_LABEL_MAP[mime] ?? mime.split('/').pop() ?? mime;

type UploadControlType = {
	handleFiles: (e:ChangeEvent<HTMLInputElement>) => void;
	fileTypeText?: string;
	progressInfo:any;
	handlePODUnavailable?: (checked: boolean) => void;
	isPODUnavailableShow?: boolean;
	acceptedFileTypes?: string[];
	maxFileSize?: string;
	additionalRestrictionsText?: string;
}

export const UploadControl = forwardRef((props:UploadControlType, ref:any) => {
	const {
		handleFiles,
		progressInfo,
		fileTypeText = 'Files to upload',
		handlePODUnavailable,
		isPODUnavailableShow,
		acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
		maxFileSize = '80',
		additionalRestrictionsText = '',
	} = props;
	const acceptAttr = acceptedFileTypes.join(', ');
	const acceptedFormatsText = [...new Set(acceptedFileTypes.map(mimeToLabel))].join(', ');
	const [dragOver, setDragOver] = useState(false);
	const { isMobile, isTablet } = useMediaQuery()
	const containerRef = useRef<HTMLDivElement>(null);
	const Upload = () => {
		if (containerRef !== null) {
			(containerRef.current?.children[2] as HTMLInputElement).focus();
			(containerRef.current?.children[2] as HTMLInputElement).click();
		}
	}

	const [checkOptions, setCheckOptions] = useState(false);

	const updatePodUnavailable = (e: ChangeEvent<HTMLInputElement>) => {
		setCheckOptions(e.target.checked);
		if (handlePODUnavailable) {
			handlePODUnavailable(e.target.checked)
		}
	};

	return (
		<>
			<div className={`${styles.container} ${dragOver ? styles.dragHover : ''}`} ref={containerRef} onDragOver={(e) => {
				e.preventDefault();
				setDragOver(true);
			}}
			onDragLeave={() => setDragOver(false)}
			onDrop={(e) => {
				e.preventDefault();
				setDragOver(false);
				if (e.dataTransfer?.files?.length) {
					const syntheticEvent = {
						...e,
						target: { files: e.dataTransfer.files }
					} as unknown as ChangeEvent<HTMLInputElement>;
					handleFiles(syntheticEvent);
				}
			}}>
				<UploadIcon className={styles.icon} />
				<div className={styles.text}>
					<p style={{ fontWeight: 700 }}>
						{
							(isMobile || isTablet) ? 'Tap to upload' : 'Drag & drop'
						} {fileTypeText}</p>

					{!isMobile && !isTablet && <p>or select upload</p>}
				</div>
				<input
					ref={ref}
					type='file'
					accept={acceptAttr}
					className={styles.input}
					multiple
					onChange={(event) => handleFiles(event)}
				/>
				{
					!isMobile && !isTablet &&
					<div style={{ position: 'relative', zIndex: 2 }}>
						<Button text={'Upload'} onClick={Upload} type={BUTTON_TYPES.SECONDARY} disabled={checkOptions} />
					</div>
				}
			</div>
			<Spacer size={10} />
			<div className={styles.infoSection}>
				<InfoOutlinedIcon className='infoIcon' />
				<Spacer size={7} dir={SPACER_DIRECTION.VERTICAL} />
				<Caption text={`Maximum file size is ${maxFileSize} MB. ${additionalRestrictionsText} Accepted file formats: ${acceptedFormatsText}.`} />
			</div>
			{isPODUnavailableShow && (<><Spacer size={10} />
				<Checkbox onChange={(e: any) => updatePodUnavailable(e)}
					name='is_pod_unavailable'
					value={'8'}
					label='POD unavailable'
					checked={checkOptions}
				/>
				<Spacer size={10} />
				{checkOptions ? <InfoChip text={<>Loads without a POD will not be paid. Read our <a target='_blank' href='https://www.ofload.com.au/carrier-terms-of-service' style={{ color: 'currentcolor' }} rel='noreferrer'>Terms & Conditions</a> to find out more.
				</>} type={CHIP_TYPE.WARN} showIcon CustomIcon={WarningAmberOutlinedIcon} /> : <InfoChip text={<>All loads require a valid POD to generate payment. Read our <a target='_blank' href='https://www.ofload.com.au/carrier-terms-of-service' style={{ color: 'currentcolor' }} rel='noreferrer'>Terms & Conditions</a> to find out more.</>} showIcon CustomIcon={InfoOutlinedIcon} />}
			</>)}
			{progressInfo && progressInfo?.map((progress: any) => {
				return (
					<>
						{progress?.is_error &&
					<InfoChip type={CHIP_TYPE.ERROR} text={`Error uploading ${progress?.fileName}.`} showIcon />}
					</>)
			})}
		</>
	);
});
